// Presence / status selector (P5) — shared constants + the idle-suppression epoch.
//
// Mirrors backend/disposition_constants.py PRESENCE_STATUSES. The status control lives in the
// admin header; the live status is server-persisted (GET/POST /api/dispositions/presence).
//
// IDLE SUPPRESSION — reuses the Phase 3.1c self-expiring cross-tab pattern (the same shape as
// TRAINING_ACTIVE_KEY in ActivityTrackerContext): while a non-Ready status is active, the
// selector writes a short, forward-dated epoch to localStorage and REFRESHES it on an interval.
// The (possibly different) leader tab reads presenceSuppressed() in its idle check and skips the
// popup. Because each write only buys PRESENCE_SUPPRESS_TTL_MS, a crashed/closed tab self-heals —
// the epoch lapses and normal idle resumes within the TTL. Bounded, self-healing, never a zombie.

export const PRESENCE_STATUSES = {
  ready: 'Ready',
  meeting: 'In a Meeting',
  break: 'On a Break',
  bathroom: 'In the Bathroom',
};

export const PRESENCE_DEFAULT = 'ready';

// Everything except Ready suppresses the idle popup (the declared status IS the answer).
export const isNonReady = (status) => !!status && status !== 'ready';

// Presentation: a small colored dot per status (R1/R2 palette; brand blue for Ready).
export const PRESENCE_DOT = {
  ready: 'bg-[#59a52c]',      // green — actively available
  meeting: 'bg-[#012697]',    // brand blue
  break: 'bg-amber-500',
  bathroom: 'bg-slate-400',
};

// ── Cross-tab idle-suppression epoch (3.1c pattern) ──────────────────────────────
export const PRESENCE_SUPPRESS_KEY = 'tc_presence_active';
// Each refresh buys this much suppression; the refresh cadence must be shorter so it never lapses
// mid-status. Short enough that a dead tab self-heals quickly.
export const PRESENCE_SUPPRESS_TTL_MS = 90 * 1000;   // 90s bought per write
export const PRESENCE_SUPPRESS_REFRESH_MS = 45 * 1000; // rewrite every 45s while non-Ready

// True while ANY tab of this user has declared a non-Ready status recently (self-expiring).
export function presenceSuppressed() {
  try {
    return Date.now() < (Number(localStorage.getItem(PRESENCE_SUPPRESS_KEY)) || 0);
  } catch {
    return false;
  }
}

export function armPresenceSuppression() {
  try {
    localStorage.setItem(PRESENCE_SUPPRESS_KEY, String(Date.now() + PRESENCE_SUPPRESS_TTL_MS));
  } catch { /* ignore */ }
}

export function clearPresenceSuppression() {
  try {
    localStorage.removeItem(PRESENCE_SUPPRESS_KEY);
  } catch { /* ignore */ }
}
